import React from "react";
import styled from "styled-components";
import { MetricsRecord } from "./ActivityChart";

interface LineBarProps {
  achieved: number;
  total: number;
  metric: string;
  isActive: boolean;
  onSelect: () => void;
  icon: string;
  backgroundImage: string; // Restored as required
  animationDelay: number;
  data: MetricsRecord[];
}

const LineBar: React.FC<LineBarProps> = ({
  achieved,
  total,
  metric,
  isActive,
  onSelect,
  icon,
  backgroundImage,
  animationDelay,
  data,
}) => {
  const percentage = Math.min((achieved / total) * 100, 100);

  const getUnitFromData = (metric: string, data: MetricsRecord[]): string => {
    const dailyMetric = data[0]?.metrics?.find((m) => m.type === "daily");
    if (!dailyMetric) return "";
    switch (metric.toLowerCase()) {
      case "steps": return dailyMetric.steps?.unit || "";
      case "distance": return dailyMetric.distance?.unit || "";
      case "calories": return dailyMetric.caloriesburned?.unit || "";
      default: return "";
    }
  };

  return (
    <StyledCard
      isActive={isActive}
      onClick={onSelect}
      backgroundImage={backgroundImage}
      animationDelay={animationDelay}
    >
      {/* Icon Circle */}
      <IconWrapper isActive={isActive}>
        <img src={icon} alt={metric} />
      </IconWrapper>

      {/* Content Section */}
      <Content>
        <HeaderRow>
          <Title>{metric}</Title>
          <Value>
            <span className="current">{achieved}</span>
            <span className="total"> / {total} {getUnitFromData(metric, data)}</span>
          </Value>
        </HeaderRow>

        {/* Progress Bar */}
        <ProgressBarBase>
          <ProgressFill percentage={percentage} />
        </ProgressBarBase>
      </Content>
    </StyledCard>
  );
};

export default LineBar;

// --- STYLED COMPONENTS (Colorful Theme Restored) ---

const StyledCard = styled.div<{ isActive: boolean; backgroundImage: string; animationDelay: number }>`
  display: flex;
  align-items: center;
  gap: 8px; /* Tighter gap */
  width: 100%;
  
  /* Mobile Padding */
  padding: 8px 10px; 
  
  box-sizing: border-box;
  border-radius: 10px;
  cursor: pointer;
  
  /* Colors */
  background-color: ${(props) => (props.isActive ? "#094752" : "#e0f7f7")};
  background-image: url(${(props) => props.backgroundImage});
  background-size: cover;
  background-position: center;
  color: ${(props) => (props.isActive ? "#FFFFFF" : "var(--text-color, #094752)")};
  
  transition: transform 0.2s ease;
  opacity: 0;
  animation: fadeIn 0.5s ease forwards;
  animation-delay: ${(props) => props.animationDelay}s;

  /* Desktop Padding */
  @media (min-width: 768px) {
    padding: 16px;
    gap: 16px;
    border-radius: 12px;
  }
  
  @keyframes fadeIn { to { opacity: 1; } }
`;

const IconWrapper = styled.div<{ isActive: boolean }>`
  /* Tiny Icon on Mobile */
  width: 28px; 
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background-color: ${(props) => (props.isActive ? "rgba(255,255,255,0.2)" : "#5DECE7")};

  img {
    width: 14px;
    height: 14px;
    filter: ${(props) => (props.isActive ? "brightness(0) invert(1)" : "none")};
  }

  /* Regular Icon on Desktop */
  @media (min-width: 768px) {
    width: 42px;
    height: 42px;
    img { width: 20px; height: 20px; }
  }
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px; /* Tighter vertical gap */
  min-width: 0; /* Allow text truncate */
  
  @media (min-width: 768px) {
    gap: 8px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  flex-wrap: nowrap; /* Prevent wrapping */
`;

const Title = styled.span`
  font-size: 11px; /* Tiny Title */
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  @media (min-width: 768px) {
    font-size: 14px;
  }
`;

const Value = styled.div`
  font-size: 10px;
  white-space: nowrap; /* Keep on one line */
  
  .current {
    font-weight: 700;
    font-size: 12px;
  }
  .total {
    opacity: 0.8;
    font-size: 9px;
  }

  @media (min-width: 768px) {
    font-size: 13px;
    .current { font-size: 16px; }
    .total { font-size: 11px; }
  }
`;

const ProgressBarBase = styled.div`
  width: 100%;
  height: 4px; /* Thinner bar on mobile */
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 10px;
  overflow: hidden;

  @media (min-width: 768px) {
    height: 6px;
  }
`;

const ProgressFill = styled.div<{ percentage: number }>`
  width: ${(props) => props.percentage}%;
  height: 100%;
  background-color: #FFFFFF; /* Always white progress bar for high contrast */
  border-radius: 10px;
  transition: width 0.5s ease;
`;